import React, { useEffect, useState, useRef, useContext, Suspense, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { PointerLockControls, Sky, Text, Stars, Preload, Billboard } from '@react-three/drei';
import { EffectComposer, Bloom, ChromaticAberration, Vignette, Noise } from '@react-three/postprocessing';
import { CharacterControls } from './CharacterControls';
import { FaCog } from 'react-icons/fa';
import { W, A, S, D, SHIFT, Z, Q } from './utils';
import './CyberpunkOffice.scss';
import MiningRigMenu from '../Menus/MiningRigMenu';
import SettingsMenu from '../Menus/SettingsMenu';
import Board from '../Menus/Board';
import mascotImage from '../../assets/reddickrednobackground.png';

// Create a context for character controls data
export const ControlsContext = React.createContext(null);

// Create a context to share the pointer lock ref
export const PointerLockContext = React.createContext(null);

// Camera controller component with improved collision detection
const CameraController = ({ initialPosition = [0, 1.7, 5], keysPressed, isMenuOpen }) => {
  const { camera, gl, scene } = useThree();
  const controlsRef = useRef(null);
  const pointerLockRef = useRef(null);
  const initialPositionRef = useRef(initialPosition);
  const [controlsData, setControlsData] = useState(null);
  const collisionObjectsRef = useRef([]);
  
  // Extract collision objects from the scene
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Find objects for collision detection - walls, furniture, etc.
    if (scene) {
      const collisionObjects = [];
      
      scene.traverse((object) => {
        // Only include meshes that should be collided with
        // Excluding the floor, ceiling, and any objects marked as non-collidable
        if (object.isMesh && 
            !object.userData.noCollision && 
            // Skip floor and ceiling by checking rotation and position
            !(Math.abs(object.rotation.x) > 1 && object.position.y < 0.1) && 
            !(Math.abs(object.rotation.x) > 1 && object.position.y > 3.9)) {
          collisionObjects.push(object);
        }
      });
      
      collisionObjectsRef.current = collisionObjects;
      
      if (controlsRef.current) {
        controlsRef.current.setCollisionObjects(collisionObjects);
      }
    }
  }, [scene]);
  
  // Initialize character controls only once per mounted canvas instance
  useEffect(() => {
    if (!controlsRef.current) {
      controlsRef.current = new CharacterControls(camera, gl.domElement, initialPositionRef.current);
      console.log('Character controls initialized');
      
      // Set collision objects if we already have them
      if (collisionObjectsRef.current.length > 0) {
        controlsRef.current.setCollisionObjects(collisionObjectsRef.current);
      }
    }
    
    // Only dispose on component unmount
    return () => {
      if (controlsRef.current) {
        controlsRef.current.dispose();
        console.log('Character controls disposed');
      }
    };
  }, [camera, gl.domElement]);
  
  // Update controls on each frame
  useFrame((state, delta) => {
    if (controlsRef.current && !isMenuOpen) {
      // Update keys state
      if (keysPressed) {
        controlsRef.current.updateKeys(keysPressed);
      }
      
      const data = controlsRef.current.update(delta);
      setControlsData(data);
    }
  });
  
  // Suppress console errors from PointerLockControls
  useEffect(() => {
    // Store the original console.error
    const originalConsoleError = console.error;
    
    // Replace console.error with a filtered version
    console.error = (...args) => {
      // Filter out PointerLockControls errors
      if (args[0] && typeof args[0] === 'string' && 
          args[0].includes('THREE.PointerLockControls: Unable to use Pointer Lock API')) {
        return;
      }
      // Pass through all other errors
      originalConsoleError.apply(console, args);
    };
    
    // Restore original on cleanup
    return () => {
      console.error = originalConsoleError;
    };
  }, []);
  
  return (
    <ControlsContext.Provider value={controlsData}>
      <PointerLockContext.Provider value={pointerLockRef}>
        <PointerLockControls ref={pointerLockRef} />
        {null /* This component doesn't render anything */}
      </PointerLockContext.Provider>
    </ControlsContext.Provider>
  );
};

// Proximity detector component
const ProximityDetector = ({ playerRef, targetPosition, threshold, onNearChange }) => {
  useFrame(() => {
    if (playerRef.current) {
      const distance = playerRef.current.distanceTo(targetPosition);
      onNearChange(distance < threshold);
    }
  });
  
  return null;
};

// Create procedural textures instead of loading from URLs
const createProceduralTexture = (type) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1024; // Higher resolution textures
  canvas.height = 1024;
  const ctx = canvas.getContext('2d');
  
  // Create sophisticated color schemes for different surfaces
  if (type === 'floor') {
    // Premium dark floor with glowing grid and accents
    const gradient = ctx.createRadialGradient(
      canvas.width/2, canvas.height/2, 0,
      canvas.width/2, canvas.height/2, canvas.width/2
    );
    gradient.addColorStop(0, '#151a2a'); // Slightly brighter center
    gradient.addColorStop(0.7, '#0f1420'); // Darker blue-gray
    gradient.addColorStop(1, '#0a0e16'); // Very dark edge
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add subtle, elegant grid
    ctx.strokeStyle = 'rgba(72, 125, 168, 0.25)'; // Fainter blue
    ctx.lineWidth = 0.75;
    const gridSize = 80; // Smaller grid
    
    for (let i = 0; i <= canvas.width; i += gridSize) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, canvas.height);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(canvas.width, i);
      ctx.stroke();
    }
    
    // Add glowing accent lines
    ctx.strokeStyle = 'rgba(0, 242, 255, 0.4)'; // Cyan glow
    ctx.lineWidth = 1.5;
    ctx.shadowColor = 'rgba(0, 242, 255, 0.7)';
    ctx.shadowBlur = 8;
    
    for (let i = gridSize * 4; i <= canvas.width; i += gridSize * 4) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, canvas.height);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.moveTo(0, i);
        ctx.lineTo(canvas.width, i);
        ctx.stroke();
    }
    ctx.shadowBlur = 0; // Reset shadow
    
    // Add subtle sparkle/noise
    for (let i = 0; i < 1500; i++) { // More sparkles
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      const size = Math.random() * 1.2 + 0.3;
      ctx.fillStyle = `rgba(150, 180, 220, ${Math.random() * 0.05 + 0.02})`;
      ctx.fillRect(x, y, size, size);
    }
  } else if (type === 'ceiling') {
    // Dark ceiling with subtle panel lines
    ctx.fillStyle = '#080a10'; // Very dark blue-black
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add panel lines
    ctx.strokeStyle = 'rgba(50, 80, 120, 0.15)'; // Very subtle lines
    ctx.lineWidth = 1;
    const panelSize = 160;
    
    for (let i = 0; i <= canvas.width; i += panelSize) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, canvas.height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(canvas.width, i);
      ctx.stroke();
    }
    // Add some random subtle tech details
     for (let i = 0; i < 50; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const size = Math.random() * 4 + 2;
        ctx.fillStyle = `rgba(50, 80, 120, ${Math.random() * 0.1 + 0.05})`;
        ctx.fillRect(x, y, size, size);
      }

  } else if (type === 'wall-main') {
    // Sophisticated dark blue-purple gradient for main walls with circuit details
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#181d35'); // Dark blue base
    gradient.addColorStop(0.5, '#1f2240'); // Slightly purple mid
    gradient.addColorStop(1, '#181d35'); // Dark blue base
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add more intricate circuit patterns
    ctx.strokeStyle = 'rgba(80, 120, 180, 0.2)'; // Fainter blue
    ctx.lineWidth = 0.75;
    
    // Main grid lines
    for (let i = 0; i < 15; i++) {
        ctx.beginPath();
        ctx.moveTo(Math.random() * canvas.width, 0);
        ctx.lineTo(Math.random() * canvas.width, canvas.height);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, Math.random() * canvas.height);
        ctx.lineTo(canvas.width, Math.random() * canvas.height);
        ctx.stroke();
    }

     // Add glowing circuit traces
    ctx.strokeStyle = 'rgba(0, 242, 255, 0.3)'; // Cyan glow lines
    ctx.lineWidth = 1;
    ctx.shadowColor = 'rgba(0, 242, 255, 0.5)';
    ctx.shadowBlur = 6;
    for (let i = 0; i < 8; i++) {
        ctx.beginPath();
        ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.stroke();
    }
    ctx.shadowBlur = 0; // Reset shadow


    // Add refined circuit nodes/dots
    for (let i = 0; i < 30; i++) { // More nodes
      const x = Math.floor(Math.random() * canvas.width);
      const y = Math.floor(Math.random() * canvas.height);
      const size = Math.floor(Math.random() * 8) + 3; // Slightly larger nodes

      // Node fill
      ctx.beginPath();
      ctx.arc(x, y, size/2, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(100, 140, 200, 0.1)';
      ctx.fill();

      // Node border
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(100, 140, 200, 0.2)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }
  } else if (type === 'wall-accent') {
    // Sophisticated dark wall with red/magenta glowing accents
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
    gradient.addColorStop(0, '#251828'); // Dark magenta/purple
    gradient.addColorStop(0.5, '#301a2a'); // Slightly brighter mid
    gradient.addColorStop(1, '#251828'); // Dark magenta/purple
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add base grid patterns
    ctx.strokeStyle = 'rgba(150, 70, 80, 0.15)'; // Fainter red
    ctx.lineWidth = 0.75;
    for (let i = 0; i < 12; i++) {
        ctx.beginPath();
        ctx.moveTo(Math.random() * canvas.width, 0);
        ctx.lineTo(Math.random() * canvas.width, canvas.height);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, Math.random() * canvas.height);
        ctx.lineTo(canvas.width, Math.random() * canvas.height);
        ctx.stroke();
    }

    // Add glowing accent lines (magenta)
    ctx.strokeStyle = 'rgba(255, 0, 229, 0.35)'; // Magenta glow
    ctx.lineWidth = 1.2;
    ctx.shadowColor = 'rgba(255, 0, 229, 0.6)';
    ctx.shadowBlur = 7;
     for (let i = 0; i < 6; i++) {
        ctx.beginPath();
        ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
        ctx.stroke();
    }
    ctx.shadowBlur = 0; // Reset shadow

    // Add tech nodes/details
    for (let i = 0; i < 25; i++) { // More nodes
      const x = Math.floor(Math.random() * canvas.width);
      const y = Math.floor(Math.random() * canvas.height);
      const size = Math.floor(Math.random() * 10) + 4;

      ctx.beginPath();
      ctx.arc(x, y, size/2, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(180, 80, 100, 0.1)';
      ctx.fill();

      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(180, 80, 100, 0.2)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }
  }
  
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  // Adjust texture repeating for better scaling
  texture.repeat.set(type === 'floor' ? 4 : 3, type === 'floor' ? 4 : 3);
  texture.anisotropy = 16; // Improve texture quality at glancing angles
  
  return texture;
};

// Office Room component with improved textures and collision
const OfficeRoom = () => {
  // Room dimensions
  const width = 20;
  const height = 4;
  const depth = 15;
  
  // Create procedural textures
  const textures = useMemo(() => {
    return {
      floor: createProceduralTexture('floor'),
      ceiling: createProceduralTexture('ceiling'),
      wallMain: createProceduralTexture('wall-main'),
      wallAccent: createProceduralTexture('wall-accent')
    };
  }, []);

  const mascotTexture = useMemo(() => new THREE.TextureLoader().load(mascotImage), []);

  return (
    <group>
      {/* Floor with enhanced texture */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow userData={{ noCollision: true }}>
        <planeGeometry args={[width, depth]} />
        <meshStandardMaterial
          map={textures.floor}
          color="#c0c0d0" // Base color tint for map
          metalness={0.6} // Slightly more metallic
          roughness={0.3} // Slightly less rough for more reflection
          envMapIntensity={1.8} // Increase environment map influence
        />
      </mesh>

      {/* Ceiling with enhanced texture and light strips */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, height, 0]} receiveShadow userData={{ noCollision: true }}>
        <planeGeometry args={[width, depth]} />
        <meshStandardMaterial
          map={textures.ceiling}
          color="#a0a0b0"
          metalness={0.3}
          roughness={0.6}
        />
      </mesh>
       {/* Ceiling Light Strips */}
       <mesh position={[0, height - 0.05, -depth/4]} rotation={[Math.PI / 2, 0, 0]} userData={{ noCollision: true }}>
            <planeGeometry args={[width * 0.8, 0.2]} />
            <meshStandardMaterial color="#00f2ff" emissive="#00f2ff" emissiveIntensity={2.5} toneMapped={false} />
       </mesh>
       <mesh position={[0, height - 0.05, depth/4]} rotation={[Math.PI / 2, 0, 0]} userData={{ noCollision: true }}>
            <planeGeometry args={[width * 0.8, 0.2]} />
            <meshStandardMaterial color="#00f2ff" emissive="#00f2ff" emissiveIntensity={2.5} toneMapped={false} />
       </mesh>
        <mesh position={[-width/4, height - 0.05, 0]} rotation={[Math.PI / 2, 0, Math.PI / 2]} userData={{ noCollision: true }}>
            <planeGeometry args={[depth * 0.8, 0.2]} />
            <meshStandardMaterial color="#00f2ff" emissive="#00f2ff" emissiveIntensity={2.0} toneMapped={false}/>
       </mesh>
        <mesh position={[width/4, height - 0.05, 0]} rotation={[Math.PI / 2, 0, Math.PI / 2]} userData={{ noCollision: true }}>
            <planeGeometry args={[depth * 0.8, 0.2]} />
            <meshStandardMaterial color="#00f2ff" emissive="#00f2ff" emissiveIntensity={2.0} toneMapped={false} />
       </mesh>


      {/* Back Wall with enhanced cyberpunk pattern */}
      <mesh position={[0, height / 2, -depth / 2]} receiveShadow>
        <planeGeometry args={[width, height]} />
        <meshStandardMaterial
          map={textures.wallMain}
          color="#b0b0c0" // Base color tint
          metalness={0.5} // Increase metalness
          roughness={0.4}
           envMapIntensity={1.2}
        />
      </mesh>
        {/* Glowing Wall Panel - Back */}
        <mesh position={[width/4, height / 2, -depth / 2 + 0.01]} >
            <planeGeometry args={[width / 5, height * 0.6]} />
            <meshStandardMaterial color="#1f2240" emissive="#00f2ff" emissiveIntensity={0.3} metalness={0.8} roughness={0.2} />
        </mesh>

      {/* Front Wall with enhanced cyberpunk pattern */}
      <mesh position={[0, height / 2, depth / 2]} rotation={[0, Math.PI, 0]} receiveShadow>
        <planeGeometry args={[width, height]} />
        <meshStandardMaterial
          map={textures.wallMain}
          color="#b0b0c0"
          metalness={0.5}
          roughness={0.4}
           envMapIntensity={1.2}
        />
      </mesh>
        {/* Glowing Wall Panel - Front */}
        <mesh position={[-width/4, height / 2, depth / 2 - 0.01]} rotation={[0, Math.PI, 0]} >
            <planeGeometry args={[width / 5, height * 0.6]} />
            <meshStandardMaterial color="#1f2240" emissive="#00f2ff" emissiveIntensity={0.3} metalness={0.8} roughness={0.2} />
        </mesh>

      {/* Left Wall with enhanced accent texture */}
      <mesh position={[-width / 2, height / 2, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[depth, height]} />
        <meshStandardMaterial
          map={textures.wallAccent}
          color="#c0b0b8" // Base color tint
          metalness={0.55} // Increase metalness
          roughness={0.4}
           envMapIntensity={1.3}
        />
      </mesh>
        {/* Glowing Wall Panel - Left */}
        <mesh position={[-width / 2 + 0.01, height / 2, -depth/4]} rotation={[0, Math.PI / 2, 0]} >
            <planeGeometry args={[depth / 3, height * 0.6]} />
            <meshStandardMaterial color="#301a2a" emissive="#ff00e5" emissiveIntensity={0.4} metalness={0.8} roughness={0.2} />
        </mesh>

      {/* Right Wall with enhanced cyberpunk pattern */}
      <mesh position={[width / 2, height / 2, 0]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[depth, height]} />
        <meshStandardMaterial
          map={textures.wallMain}
          color="#b0b0c0"
          metalness={0.5}
          roughness={0.4}
           envMapIntensity={1.2}
        />
      </mesh>
              {/* Glowing Wall Panel - Right */}
      <mesh position={[width / 2 - 0.01, height / 2, depth/4]} rotation={[0, -Math.PI / 2, 0]} >
        <planeGeometry args={[depth / 3, height * 0.6]} />
        <meshStandardMaterial color="#1f2240" emissive="#00f2ff" emissiveIntensity={0.3} metalness={0.8} roughness={0.2} />
      </mesh>

      {/* Mascot Picture on Back Wall */}
      <group position={[-width / 4, height / 2, -depth / 2 + 0.3]}>
        {/* Picture Frame */}
        <mesh>
          <boxGeometry args={[2.2, 2.2, 0.05]} />
          <meshStandardMaterial color="#080a10" metalness={0.9} roughness={0.1} />
        </mesh>
        {/* Glowing backdrop */}
        <mesh position={[0, 0, 0.02]}>
          <planeGeometry args={[2.1, 2.1]} />
          <meshStandardMaterial color="#00f2ff" emissive="#00f2ff" emissiveIntensity={0.4} transparent opacity={0.3} toneMapped={false} />
        </mesh>
        {/* Picture */}
        <mesh position={[0, 0, 0.03]}>
          <planeGeometry args={[2, 2]} />
          <meshStandardMaterial map={mascotTexture} transparent={true} />
        </mesh>
      </group>

      {/* Mascot Picture on Left Wall */}
      <group position={[-width / 2 + 0.3, height / 2, 4.5]} rotation={[0, Math.PI / 2, 0]}>
        {/* Picture Frame */}
        <mesh>
          <boxGeometry args={[2.2, 2.2, 0.05]} />
          <meshStandardMaterial color="#080a10" metalness={0.9} roughness={0.1} />
        </mesh>
        {/* Glowing backdrop */}
        <mesh position={[0, 0, 0.02]}>
          <planeGeometry args={[2.1, 2.1]} />
          <meshStandardMaterial color="#00f2ff" emissive="#00f2ff" emissiveIntensity={0.4} transparent opacity={0.3} toneMapped={false} />
        </mesh>
        {/* Picture */}
        <mesh position={[0, 0, 0.03]}>
          <planeGeometry args={[2, 2]} />
          <meshStandardMaterial map={mascotTexture} transparent={true} />
        </mesh>
      </group>

      {/* Collision boxes for walls - invisible barriers */}
      {/* Back wall collision */}
      <mesh position={[0, height / 2, -depth / 2]} visible={false}>
        <boxGeometry args={[width, height, 0.5]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>
      
      {/* Front wall collision */}
      <mesh position={[0, height / 2, depth / 2]} visible={false}>
        <boxGeometry args={[width, height, 0.5]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>
      
      {/* Left wall collision - Make thinner */}
      <mesh position={[-width / 2, height / 2, 0]} visible={false}>
        <boxGeometry args={[0.1, height, depth]} /> {/* Reduced X dimension from 0.5 to 0.1 */}
        <meshBasicMaterial transparent opacity={0} />
      </mesh>
      
      {/* Right wall collision */}
      <mesh position={[width / 2, height / 2, 0]} visible={false}>
        <boxGeometry args={[0.5, height, depth]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {/* Updated office desk */}
      <group position={[-6, 0, -4]}>
        {/* Desk surface - More angular */}
        <mesh position={[0, 0.75, 0]} castShadow receiveShadow>
          <boxGeometry args={[2.8, 0.1, 1.4]} /> {/* Wider, deeper */}
          <meshStandardMaterial color="#101420" metalness={0.7} roughness={0.3} /> {/* Darker, more metallic */}
        </mesh>
        
        {/* Desk legs - Angled */}
         <mesh position={[-1.2, 0.375, -0.6]} rotation={[0, 0, 0.1]} castShadow receiveShadow>
            <boxGeometry args={[0.15, 0.75, 0.15]} />
            <meshStandardMaterial color="#080a10" metalness={0.8} roughness={0.2} />
        </mesh>
         <mesh position={[1.2, 0.375, -0.6]} rotation={[0, 0, -0.1]} castShadow receiveShadow>
            <boxGeometry args={[0.15, 0.75, 0.15]} />
            <meshStandardMaterial color="#080a10" metalness={0.8} roughness={0.2} />
        </mesh>
        <mesh position={[-1.2, 0.375, 0.6]} rotation={[0, 0, 0.1]} castShadow receiveShadow>
            <boxGeometry args={[0.15, 0.75, 0.15]} />
            <meshStandardMaterial color="#080a10" metalness={0.8} roughness={0.2} />
        </mesh>
         <mesh position={[1.2, 0.375, 0.6]} rotation={[0, 0, -0.1]} castShadow receiveShadow>
            <boxGeometry args={[0.15, 0.75, 0.15]} />
            <meshStandardMaterial color="#080a10" metalness={0.8} roughness={0.2} />
        </mesh>

        {/* Curved Computer monitor */}
        <mesh position={[0, 1.25, -0.45]} rotation={[0.1, 0, 0]} castShadow> {/* Slightly tilted up */}
           {/* Using Cylinder for curved effect */}
          <cylinderGeometry args={[4, 4, 0.8, 64, 1, true, Math.PI * 0.9, Math.PI * 0.2]} />
          <meshStandardMaterial color="#080a10" metalness={0.9} roughness={0.1} side={THREE.DoubleSide} />
        </mesh>
         {/* Monitor Stand */}
        <mesh position={[0, 0.8, -0.6]} castShadow receiveShadow>
            <boxGeometry args={[0.2, 0.1, 0.2]} />
            <meshStandardMaterial color="#080a10" metalness={0.8} roughness={0.2} />
        </mesh>
         <mesh position={[0, 1.0, -0.6]} castShadow receiveShadow>
            <boxGeometry args={[0.1, 0.4, 0.1]} />
            <meshStandardMaterial color="#080a10" metalness={0.8} roughness={0.2} />
        </mesh>

        {/* Monitor screen (Plane inside the curve) */}
        <mesh position={[0, 1.25, -0.4]} rotation={[0.1, 0, 0]} >
          <planeGeometry args={[1.4, 0.7]} /> {/* Wider screen */}
          <meshStandardMaterial color="#00f2ff" emissive="#00f2ff" emissiveIntensity={1.5} toneMapped={false} side={THREE.DoubleSide} />
        </mesh>

        {/* Keyboard - Thinner with emissive keys */}
        <mesh position={[0, 0.81, 0.1]} castShadow> {/* Moved forward */}
          <boxGeometry args={[0.9, 0.03, 0.4]} /> {/* Thinner, wider */}
          <meshStandardMaterial color="#080a10" metalness={0.6} roughness={0.4} />
        </mesh>
         {/* Keyboard glow */}
         <mesh position={[0, 0.815, 0.1]} >
             <planeGeometry args={[0.85, 0.35]} />
             <meshStandardMaterial color="#101420" emissive="#ff00e5" emissiveIntensity={0.5} toneMapped={false} />
         </mesh>

         {/* Futuristic Chair */}
        <group position={[0, 0, 0.8]} rotation={[0, Math.PI * 0.1, 0]}>
            {/* Seat Base */}
            <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
                <boxGeometry args={[0.6, 0.1, 0.6]} />
                <meshStandardMaterial color="#181d35" metalness={0.7} roughness={0.3} />
            </mesh>
            {/* Back Rest */}
             <mesh position={[0, 0.9, -0.25]} rotation={[0.2, 0, 0]} castShadow receiveShadow>
                <boxGeometry args={[0.55, 0.7, 0.08]} />
                <meshStandardMaterial color="#181d35" metalness={0.7} roughness={0.3} />
            </mesh>
             {/* Central Column */}
            <mesh position={[0, 0.25, 0]} castShadow receiveShadow>
                <cylinderGeometry args={[0.05, 0.07, 0.5, 16]} />
                <meshStandardMaterial color="#080a10" metalness={0.9} roughness={0.2} />
            </mesh>
            {/* Legs */}
            {[0, 1, 2, 3, 4].map(i => (
                 <mesh key={i} position={[Math.sin(i * Math.PI * 2 / 5) * 0.25, 0.05, Math.cos(i * Math.PI * 2 / 5) * 0.25]} rotation={[0, i * Math.PI * 2 / 5, 1.3]} castShadow receiveShadow>
                    <boxGeometry args={[0.4, 0.04, 0.04]} />
                    <meshStandardMaterial color="#080a10" metalness={0.9} roughness={0.2} />
                </mesh>
            ))}
        </group>
      </group>

      {/* Interactive Board on Left Wall - ENHANCED VISIBILITY */}
      <mesh position={[-width / 2 + 0.1, 1.5, 0]} rotation={[0, Math.PI / 2, 0]} castShadow receiveShadow>
        <planeGeometry args={[2, 1.5]} /> {/* LARGER SIZE */}
        <meshStandardMaterial color="#2a2a3a" metalness={0.8} roughness={0.3} />
      </mesh>
      {/* Board Frame */}
      <mesh position={[-width / 2 + 0.12, 1.5, 0]} rotation={[0, Math.PI / 2, 0]}>
          <boxGeometry args={[2.2, 1.7, 0.05]} /> {/* LARGER SIZE */}
          <meshStandardMaterial color="#080a10" metalness={0.9} roughness={0.2} />
      </mesh>
      {/* Add a STRONG glow to the board */}
      <mesh position={[-width / 2 + 0.13, 1.5, 0]} rotation={[0, Math.PI / 2, 0]}>
          <planeGeometry args={[1.9, 1.4]} />
          <meshStandardMaterial color="#1f2240" emissive="#00aaff" emissiveIntensity={1.5} toneMapped={false} /> {/* MUCH BRIGHTER */}
      </mesh>
      {/* Add TEXT label to the board */}
      <group position={[-width / 2 + 0.15, 1.9, 0]} rotation={[0, Math.PI / 2, 0]}>
          <mesh>
              <planeGeometry args={[1.2, 0.2]} /> 
              <meshStandardMaterial color="#080a10" emissive="#00ffff" emissiveIntensity={2} toneMapped={false} />
          </mesh>
      </group>
    </group>
  );
};

// Enhanced Datacenter Racks with collision detection
const DatacenterRacks = () => {
  // Create a blinking light effect
  const blinkingLights = useRef([]);
  
  useFrame(({ clock }) => {
    blinkingLights.current.forEach((light, i) => {
      if (light) {
        // Random blinking pattern
        const time = clock.getElapsedTime();
        const blink = Math.sin(time * (2 + i * 0.2)) * 0.5 + 0.5;
        light.material.emissiveIntensity = blink * 2;
      }
    });
  });
  
  return (
    <group position={[8, 0, 0]}>
      {/* Server rack cabinet with collision */}
      <mesh position={[0, 1.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[3, 3, 1.2]} />
        <meshStandardMaterial color="#0c0c14" metalness={0.9} roughness={0.2} />
      </mesh>
      
      {/* Server units */}
      {[...Array(6)].map((_, index) => (
        <group key={index} position={[0, 0.5 + index * 0.4, 0]}>
          {/* Server unit */}
          <mesh position={[0, 0, 0.62]} castShadow receiveShadow>
            <boxGeometry args={[2.8, 0.35, 0.05]} />
            <meshStandardMaterial color="#1b1c33" metalness={0.9} roughness={0.1} />
          </mesh>
          
          {/* Server front panel */}
          <mesh position={[0, 0, 0.65]} castShadow>
            <boxGeometry args={[2.7, 0.3, 0.01]} />
            <meshStandardMaterial color="#1f2040" metalness={0.8} roughness={0.2} />
          </mesh>
          
          {/* Server LED indicators */}
          {[...Array(4)].map((_, i) => (
            <mesh 
              key={i} 
              position={[-1.2 + i * 0.8, 0, 0.66]} 
              ref={el => {
                if (el) blinkingLights.current[index * 4 + i] = el;
              }}
            >
              <boxGeometry args={[0.05, 0.05, 0.01]} />
              <meshStandardMaterial 
                color={i % 2 === 0 ? "#00ff9d" : "#ff00e5"} 
                emissive={i % 2 === 0 ? "#00ff9d" : "#ff00e5"} 
                emissiveIntensity={2} 
              />
            </mesh>
          ))}
        </group>
      ))}
      
      {/* Second server rack */}
      <group position={[0, 0, -3]}>
        <mesh position={[0, 1.5, 0]} castShadow receiveShadow>
          <boxGeometry args={[3, 3, 1.2]} />
          <meshStandardMaterial color="#0c0c14" metalness={0.9} roughness={0.2} />
        </mesh>
        
        {/* Server units */}
        {[...Array(6)].map((_, index) => (
          <group key={index} position={[0, 0.5 + index * 0.4, 0]}>
            {/* Server unit */}
            <mesh position={[0, 0, 0.62]} castShadow receiveShadow>
              <boxGeometry args={[2.8, 0.35, 0.05]} />
              <meshStandardMaterial color="#1b1c33" metalness={0.9} roughness={0.1} />
            </mesh>
            
            {/* Server front panel */}
            <mesh position={[0, 0, 0.65]} castShadow>
              <boxGeometry args={[2.7, 0.3, 0.01]} />
              <meshStandardMaterial color="#1f2040" metalness={0.8} roughness={0.2} />
            </mesh>
            
            {/* Server LED indicators */}
            {[...Array(4)].map((_, i) => (
              <mesh 
                key={i} 
                position={[-1.2 + i * 0.8, 0, 0.66]} 
                ref={el => {
                  if (el) blinkingLights.current[(index + 6) * 4 + i] = el;
                }}
              >
                <boxGeometry args={[0.05, 0.05, 0.01]} />
                <meshStandardMaterial 
                  color={i % 2 === 0 ? "#00ff9d" : "#f6ff00"} 
                  emissive={i % 2 === 0 ? "#00ff9d" : "#f6ff00"} 
                  emissiveIntensity={2} 
                />
              </mesh>
            ))}
          </group>
        ))}
      </group>
      
      {/* Cooling unit */}
      <group position={[0, 0, 3]}>
        <mesh position={[0, 1.5, 0]} castShadow receiveShadow>
          <boxGeometry args={[2, 3, 1]} />
          <meshStandardMaterial color="#1b1c33" metalness={0.7} roughness={0.3} />
        </mesh>
        
        {/* Cooling vents */}
        {[...Array(5)].map((_, i) => (
          <mesh key={i} position={[0, 0.5 + i * 0.5, 0.51]} castShadow>
            <planeGeometry args={[1.8, 0.4]} />
            <meshStandardMaterial color="#0c0c14" metalness={0.8} roughness={0.2} />
          </mesh>
        ))}
        
        {/* Status display */}
        <mesh position={[0, 2.5, 0.51]}>
          <planeGeometry args={[1, 0.3]} />
          <meshStandardMaterial color="#00f2ff" emissive="#00f2ff" emissiveIntensity={1.0} />
        </mesh>
      </group>
      
      {/* Floor cable management - mark as non-collidable */}
      <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow userData={{ noCollision: true }}>
        <planeGeometry args={[4, 8]} />
        <meshStandardMaterial color="#0c0c14" metalness={0.7} roughness={0.3} />
      </mesh>
    </group>
  );
};

// Empty component to replace all decorations
const CyberpunkDecorations = () => {
  return null; // Return nothing - no decorative elements
};

// New Approach Panel Component
const ApproachPanel = ({ visible, position, text = "Approach to open your rig menu" }) => {
  if (!visible) return null;

  return (
    <Billboard position={position}>
      <Text
        fontSize={0.3} // Adjust size as needed
        color="#00f2ff" // Cyan color like other UI elements
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.01}
        outlineColor="#000000"
        maxWidth={4} // Prevent text wrapping too wide
        textAlign="center"
      >
        {text}
      </Text>
    </Billboard>
  );
};

// Mining Rig Interaction Button Component that appears when player is near
const MiningRigButton = ({ isNearMiningRig, onClick }) => {
  if (!isNearMiningRig) return null;
  
  return (
    <div className="mining-rig-button-container">
      <button className="mining-rig-button" onClick={onClick} aria-label="Mining Rigs">
        Mining Rigs
      </button>
    </div>
  );
};

// Board Interaction Button Component
const BoardButton = ({ isNearBoard, onClick }) => {
  if (!isNearBoard) return null;

  return (
    <div className="board-button-container">
      <button className="board-button" onClick={onClick}>
        Board
      </button>
    </div>
  );
};

// Debug overlay component
const DebugOverlay = ({ isLocked, keys, playerPos, gameData }) => {
  const controlsData = useContext(ControlsContext);
  
  if (!controlsData) return null;
  
  return (
    <div className="debug-overlay">
      <div>Position: 
        X: {controlsData.position.x}, 
        Y: {controlsData.position.y}, 
        Z: {controlsData.position.z}
      </div>
      <div>Action: {controlsData.action}</div>
      <div>Direction: 
        X: {controlsData.direction.x}, 
        Z: {controlsData.direction.z}
      </div>
      <div>Keys: {Object.entries(controlsData.keysPressed)
        .filter(([_, pressed]) => pressed)
        .map(([key]) => key)
        .join(', ')}
      </div>
      <div>Player Position: {playerPos.x.toFixed(2)}, {playerPos.y.toFixed(2)}, {playerPos.z.toFixed(2)}</div>
      <div>Game Data: {JSON.stringify(gameData)}</div>
    </div>
  );
};

// Settings Icon Component
const SettingsIcon = ({ onClick }) => {
  return (
    <div className="settings-icon-container">
      <div className="settings-tooltip">
        Push mouse middle button to free cursor
      </div>
      <button className="settings-icon" onClick={onClick} aria-label="Settings">
        <FaCog />
      </button>
    </div>
  );
};

// Wrap CyberpunkOffice with React.memo for performance
const CyberpunkOffice = React.memo(({ onSceneLoaded, gameData, updateGameData, reloadGameData, refreshMoneyDisplay }) => {
  console.log("CyberpunkOffice component rendering with props:", { 
    hasGameData: !!gameData,
    hasUpdateCallback: !!updateGameData,
    hasRefreshCallback: !!refreshMoneyDisplay
  });

  // Initialize refs
  const gameDataRef = useRef(gameData);
  const [showControls, setShowControls] = useState(true);
  const [showDebug, setShowDebug] = useState(true);
  const [isLocked, setIsLocked] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showMiningRigMenu, setShowMiningRigMenu] = useState(false);
  const [isNearMiningRig, setIsNearMiningRig] = useState(false);
  const [keysPressed, setKeysPressed] = useState({});
  const playerPositionRef = useRef(new THREE.Vector3(0, 1.7, 5));
  const [showApproachPanel, setShowApproachPanel] = useState(false); // State for the new panel
  const [isWithinPanelRange, setIsWithinPanelRange] = useState(false); // State for the wider range
  const [isNearBoard, setIsNearBoard] = useState(false); // State for board proximity
  const [showBoardMenu, setShowBoardMenu] = useState(false); // State for board menu visibility
  const [showBoardPanel, setShowBoardPanel] = useState(false); // State for board approach panel
  const [isWithinBoardRange, setIsWithinBoardRange] = useState(false); // State for wider board range
  
  // Update the ref when gameData changes
  useEffect(() => {
    gameDataRef.current = gameData;
  }, [gameData]);

  const calculateMiningPower = useCallback((rigs) => {
    if (!Array.isArray(rigs)) return 0;
    return rigs.reduce((total, rig) => {
      if (rig.isActive) {
        const tierPowers = gameDataRef.current?.constants?.RIG_TIER_POWERS || [10, 15, 25, 40];
        return total + tierPowers[rig.tier - 1];
      }
      return total;
    }, 0);
  }, []);
  
  // Create a safe update function that doesn't cause re-renders but still updates the database
  const safeUpdateGameData = useCallback((updatedData) => {
    if (!updatedData || !updatedData.data || !gameDataRef.current?.data) {
      console.error("Invalid data passed to safeUpdateGameData");
      return;
    }
    
    // Extract only the specific data we need to update
    const miningRigs = updatedData.data.miningRigs;
    const money = updatedData.money;
    
    // Validate the data
    if (!Array.isArray(miningRigs) || typeof money !== 'number') {
      console.error("Invalid mining rig or money data:", { miningRigs, money });
      return;
    }

    try {
      // Get current values to validate changes
      // Validate money values
      if (money < 0) {
        console.error("Negative money value detected, skipping update");
        return;
      }
      
      // Validate mining rig values
      const maxAllowedRigs = 4; // Maximum allowed rigs (should match MiningRigMenu)
      if (miningRigs.length > maxAllowedRigs) {
        console.error(`Invalid mining rig count: ${miningRigs.length}, skipping update`);
        return;
      }
      
      // Create a minimal update object with just the essential data
      // This helps prevent unnecessary re-renders while still updating the DB
      const dbUpdateObj = {
        data: {
          ...gameDataRef.current.data,
          miningRigs,
          energy: gameDataRef.current.data.energy || 100
        },
        money,
        mining_power: calculateMiningPower(miningRigs)
      };

      updateGameData(dbUpdateObj);
      
      // Also explicitly refresh the money display to ensure UI consistency
      if (refreshMoneyDisplay) {
        refreshMoneyDisplay(money);
      }
    } catch (error) {
      console.error("Error in safeUpdateGameData:", error);
    }
  }, [calculateMiningPower, updateGameData, refreshMoneyDisplay]);
  
  // Debug positions when requested
  const DEBUG_POSITIONS = false;
  
  // Position of the datacenter/mining rigs - match the actual position in the scene
  const miningRigPosition = useMemo(() => new THREE.Vector3(8, 1.5, 0), []);
  // Position of the interactive board - align exactly with the mesh
  const boardPosition = useMemo(() => new THREE.Vector3(-9.9, 1.5, 0), []); // Match the board mesh position
  // Proximity thresholds
  const proximityThreshold = 5;
  const panelVisibilityThreshold = 10; // Wider threshold for the approach panel
  const boardProximityThreshold = 6; // INCREASED threshold for the board interaction to make it easier to trigger
  const boardVisibilityThreshold = 12; // Wider threshold for board panel visibility
  
  // Always ensure pointer is unlocked when opening any menu
  const openMenu = () => {
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
    setShowMenu(true);
  };
  
  // Open mining rig menu and unlock pointer
  const openMiningRigMenu = () => {
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
    setShowMiningRigMenu(true);
  };

  // Open board menu and unlock pointer
  const openBoardMenu = () => {
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }
    setShowBoardMenu(true);
  };

  // Just close the menu without affecting pointer lock
  const closeMenu = () => {
    setShowMenu(false);
  };

  // Close mining rig menu
  const closeMiningRigMenu = () => {
    setShowMiningRigMenu(false);
  };

  // Close board menu
  const closeBoardMenu = () => {
    setShowBoardMenu(false);
    // Optionally re-lock pointer if desired after closing
    // const canvas = document.querySelector('canvas');
    // if (canvas) canvas.requestPointerLock();
  };

  const handleKeyDown = useCallback((e) => {
    const key = e.key.toLowerCase();
    
    // Only process game controls when not in any menu and pointer is locked
    if (!showMenu && !showMiningRigMenu && !showBoardMenu && document.pointerLockElement && [W, A, S, D, SHIFT, Z, Q, '`'].includes(key)) {
      e.preventDefault();
      if (key === '`') {
        setShowDebug(prev => !prev);
        return;
      }
      setKeysPressed(prev => ({ ...prev, [key]: true }));
    }
  }, [showBoardMenu, showMenu, showMiningRigMenu]);

  const handleKeyUp = useCallback((e) => {
    const key = e.key.toLowerCase();
    if (!showMenu && !showMiningRigMenu && !showBoardMenu && document.pointerLockElement && [W, A, S, D, SHIFT, Z, Q].includes(key)) {
      setKeysPressed(prev => ({ ...prev, [key]: false }));
    }
  }, [showBoardMenu, showMenu, showMiningRigMenu]);

  const handleCanvasClick = () => {
    // Only request pointer lock if no menu is open
    if (!showMenu && !showMiningRigMenu && !showBoardMenu && !document.pointerLockElement) {
      console.log('Canvas clicked, requesting pointer lock');
      const canvas = document.querySelector('canvas');
      if (canvas && canvas.isConnected) {
        try {
          canvas.requestPointerLock();
          canvas.focus();
        } catch (err) {
          console.error('Error requesting pointer lock:', err);
        }
      }
    }
  };

  const handleMouseDown = useCallback((e) => {
    // Middle mouse button (button 1) toggles pointer lock ONLY when no menu is open
    if (e.button === 1 && !showMenu && !showMiningRigMenu && !showBoardMenu) {
      if (document.pointerLockElement) {
        console.log('Middle mouse button pressed, exiting pointer lock');
        document.exitPointerLock();
      } else {
        console.log('Middle mouse button pressed, requesting pointer lock');
        const canvas = document.querySelector('canvas');
        if (canvas && canvas.isConnected) {
          canvas.requestPointerLock();
        }
      }
    }
  }, [showBoardMenu, showMenu, showMiningRigMenu]);

  // Calculate visibility for the approach panel
  useEffect(() => {
    setShowApproachPanel(isWithinPanelRange && !isNearMiningRig);
    setShowBoardPanel(isWithinBoardRange && !isNearBoard);
  }, [isWithinPanelRange, isNearMiningRig, isWithinBoardRange, isNearBoard]);

  // Setup effects for scene loading and event listeners
  useEffect(() => {
    console.log("Setting up CyberpunkOffice component");

    const controlsTimer = setTimeout(() => setShowControls(false), 5000);

    // Set up pointer lock event listeners
    const handlePointerLockChange = () => {
      const locked = document.pointerLockElement !== null;
      setIsLocked(locked);
      
      // If any menu is open and pointer gets locked somehow, unlock it immediately
      if (locked && (showMenu || showMiningRigMenu || showBoardMenu)) {
        document.exitPointerLock();
      }
    };

    const handlePointerLockError = () => {
      console.error('Pointer lock request failed');
    };

    // Add all event listeners
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('pointerlockchange', handlePointerLockChange);
    document.addEventListener('pointerlockerror', handlePointerLockError);

    // Clean up everything when component unmounts
    return () => {
      clearTimeout(controlsTimer);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('pointerlockchange', handlePointerLockChange);
      document.removeEventListener('pointerlockerror', handlePointerLockError);
    };
  }, [handleKeyDown, handleKeyUp, handleMouseDown, onSceneLoaded, showMenu, showMiningRigMenu, showBoardMenu]); // Keep important dependencies

  return (
    <div className="cyberpunk-office" tabIndex={0}>
      <Canvas 
        shadows 
        style={{ background: '#0c0c14', width: '100%', height: '100vh', display: 'block' }} 
        camera={{ fov: 75, near: 0.1, far: 1000, position: [0, 1.7, 5] }} 
        onClick={handleCanvasClick}
        onCreated={({ gl, scene }) => {
          console.log("Canvas created successfully");
          gl.setClearColor('#0c0c14');
          gl.setPixelRatio(Math.min(window.devicePixelRatio, 2));

          setTimeout(() => {
            if (onSceneLoaded) {
              console.log("Canvas creation complete, calling onSceneLoaded");
              onSceneLoaded();
            }
          }, 500);
        }}
        gl={{ 
          antialias: true,
          alpha: false,
          powerPreference: "high-performance"
        }}
      >
        <CameraController keysPressed={keysPressed} isMenuOpen={showMenu || showMiningRigMenu || showBoardMenu} />
        
        {/* Track player position */}
        <PlayerPositionObserver positionRef={playerPositionRef} />
        
        {/* Check proximity to mining rig */}
        <ProximityDetector 
          playerRef={playerPositionRef}
          targetPosition={miningRigPosition}
          threshold={proximityThreshold}
          onNearChange={setIsNearMiningRig}
        />
        
        {/* Check proximity for the approach panel visibility */}
        <ProximityDetector
          playerRef={playerPositionRef}
          targetPosition={miningRigPosition}
          threshold={panelVisibilityThreshold}
          onNearChange={setIsWithinPanelRange} // Set the wider range state
        />
        
        {/* Check proximity for the board interaction */}
        <ProximityDetector
          playerRef={playerPositionRef}
          targetPosition={boardPosition}
          threshold={boardProximityThreshold}
          onNearChange={setIsNearBoard}
        />
        
        {/* Check proximity for the board approach panel */}
        <ProximityDetector
          playerRef={playerPositionRef}
          targetPosition={boardPosition}
          threshold={boardVisibilityThreshold}
          onNearChange={setIsWithinBoardRange}
        />
        
        {/* Improved Lighting */}
        <ambientLight intensity={25.0} color="#8090ff" /> {/* Cooler ambient, boosted further */}

        {/* Main room light - Slightly adjusted */}
        <pointLight position={[0, 3.5, 0]} intensity={7.0} color="#80f8ff" castShadow shadow-mapSize={[1024, 1024]} shadow-bias={-0.0005}/> {/* Cyan/White, boosted */}

        {/* Datacenter area lighting - Enhanced */}
        <pointLight position={[8, 2.5, 0]} intensity={10.0} distance={15} decay={1.5} color="#ff40ff" castShadow shadow-mapSize={[1024, 1024]} shadow-bias={-0.0005} /> {/* Brighter Magenta, boosted */}

        {/* Desk area lighting - Enhanced */}
        <pointLight position={[-6, 2.5, -4]} intensity={5.0} distance={12} decay={1.5} color="#40ffc0" castShadow shadow-mapSize={[1024, 1024]} shadow-bias={-0.0005} /> {/* Brighter Teal, boosted */}


        {/* Additional fill lights */}
        <pointLight position={[-8, 3, -5]} intensity={1.0} distance={10} decay={1.8} color="#ffffff" />
        <pointLight position={[8, 3, -5]} intensity={1.0} distance={10} decay={1.8} color="#ffffff" />
        <pointLight position={[-8, 3, 5]} intensity={1.0} distance={10} decay={1.8} color="#ffffff" />
        <pointLight position={[8, 3, 5]} intensity={1.0} distance={10} decay={1.8} color="#ffffff" />

        {/* Post-processing */}
        <EffectComposer>
          <Bloom luminanceThreshold={0.3} luminanceSmoothing={0.9} intensity={0.8} radius={0.8} /> {/* Reduced intensity */}
          <ChromaticAberration offset={[0.001, 0.001]} />
          <Vignette eskil={false} offset={0.1} darkness={0.5} /> {/* Slightly reduced darkness */}
          <Noise opacity={0.02} />
        </EffectComposer>

        <Suspense fallback={null}>
          <OfficeRoom />
          <DatacenterRacks />
          <CyberpunkDecorations />
          <ApproachPanel visible={showApproachPanel} position={[5.5, 2.5, 0]} text="Approach to open your rig menu" /> {/* Position it left of the center datacenter */}
          <ApproachPanel visible={showBoardPanel} position={[-8.3, 2.5, 0]} text="Approach to use information board" /> {/* Position it near the board */}
          <Sky distance={450000} sunPosition={[0, 1, 0]} turbidity={10} rayleigh={3} mieCoefficient={0.005} mieDirectionalG={0.7} />
          <Stars radius={300} depth={60} count={20000} factor={7} saturation={0} fade speed={1} />
        </Suspense>

        <Preload all />
      </Canvas>
      
      {showControls && !showMenu && !showMiningRigMenu && !showBoardMenu && <div className="controls-hint">Use W/A/S/D or Z/Q/S/D to move, hold SHIFT to run<br />Click to lock mouse and look around<br />Middle mouse button toggles cursor lock (when not in menu)<br />Press ` to toggle debug info</div>}
      {!isLocked && !showMenu && !showMiningRigMenu && !showBoardMenu && <div className="click-to-play" onClick={handleCanvasClick}>Click to play</div>}
      {showDebug && !showMenu && !showMiningRigMenu && !showBoardMenu && <DebugOverlay isLocked={isLocked} keys={keysPressed} playerPos={playerPositionRef.current} gameData={gameData} />}
      {!showMenu && !showMiningRigMenu && !showBoardMenu && <SettingsIcon onClick={openMenu} />}
      {showMenu && (
        <SettingsMenu 
          gameSave={gameData} // Pass the whole gameSave object
          onClose={closeMenu} 
          reloadGameData={reloadGameData}
          refreshMoneyDisplay={refreshMoneyDisplay} // Pass the refresh function
          // Removed onExitGame - handled internally now
        />
      )}
      
      {/* Mining Rig Button - Debug info */}
      {DEBUG_POSITIONS && !showMenu && !showMiningRigMenu && !showBoardMenu && (
        <div className="debug-position" style={{position: 'absolute', top: '80px', right: '10px', background: 'rgba(0,0,0,0.7)', color: 'white', padding: '5px', zIndex: 100}}>
          Near Mining Rig: {isNearMiningRig ? 'Yes' : 'No'}<br/>
          Player Pos: {playerPositionRef.current.x.toFixed(2)}, {playerPositionRef.current.y.toFixed(2)}, {playerPositionRef.current.z.toFixed(2)}
        </div>
      )}
      
      {/* Mining Rig Button (only visible when near mining rigs) */}
      {!showMenu && !showMiningRigMenu && !showBoardMenu &&
        <MiningRigButton
          isNearMiningRig={isNearMiningRig}
          onClick={openMiningRigMenu}
        />
      }

      {/* Board Interaction Button (only visible when near the board) */}
      {!showMenu && !showMiningRigMenu && !showBoardMenu &&
        <BoardButton
          isNearBoard={isNearBoard}
          onClick={openBoardMenu}
        />
      }
      
      {/* Mining Rig Menu */}
      {showMiningRigMenu && (
        <MiningRigMenu 
          gameData={gameData} 
          updateGameData={safeUpdateGameData} 
          onClose={closeMiningRigMenu} 
          refreshMoneyDisplay={refreshMoneyDisplay}
        />
      )}

      {/* Board Menu */}
      {showBoardMenu && (
        <Board onClose={closeBoardMenu} gameData={gameData} />
      )}
    </div>
  );
});

// Add a custom equality function to prevent unnecessary re-renders
CyberpunkOffice.displayName = 'CyberpunkOffice';

// Helper component to simply track camera position
const PlayerPositionObserver = ({ positionRef }) => {
  const { camera } = useThree();
  
  useFrame(() => {
    if (camera) {
      positionRef.current.copy(camera.position);
    }
  });
  
  return null;
};

export default CyberpunkOffice; 
